// How a contact is named wherever the dashboard lists people: the builder, Outreach, the best
// person card, hover cards and the push panel. Pure, so `node --test` covers it.
//
// A real name wins. With none (or only an address in the name field), a `first.last` or
// `first_last` address becomes "First Last"; anything else stays the address, shown muted.
// Shared inboxes (sales@, support@, ...) are not people and are hidden or set aside.

// Words that name a team or a function, never a person. Compound inboxes match too, with or
// without separators: `contact-us`, `dev.rel`, `account_manager`, `customersuccess`.
const SHARED_INBOX_LOCAL_PARTS = new Set([
  'abuse', 'account', 'accounting', 'accountmanager', 'accounts', 'admin', 'api', 'billing', 'bookings', 'careers',
  'community', 'contact', 'contactus', 'customer', 'customercare', 'customers', 'customerservice', 'customersuccess',
  'developers', 'devrel', 'engineering', 'enquiries', 'enquiry', 'events', 'feedback', 'finance', 'general',
  'hello', 'help', 'helpdesk', 'hi', 'hr', 'info', 'inquiries', 'inquiry', 'it', 'jobs', 'legal', 'mail', 'marketing',
  'media', 'news', 'notifications', 'office', 'ops', 'operations', 'order', 'orders', 'partners', 'partnerships',
  'postmaster', 'press', 'privacy', 'recruiting', 'reservations', 'sales', 'security', 'service', 'services',
  'success', 'support', 'team', 'tech', 'us', 'webmaster',
  // Role inboxes that spell two words: investor.relations@, talent.acquisition@, people.ops@ (GRO-296).
  'acquisition', 'compliance', 'hiring', 'human', 'investor', 'investorrelations', 'investors', 'ir', 'payable',
  'payables', 'payroll', 'people', 'peopleops', 'procurement', 'purchasing', 'receivable', 'receivables',
  'recruitment', 'relations', 'resources', 'talent', 'talentacquisition',
]);

// Any no-reply spelling: noreply, no-reply, no_reply, donotreply, do-not-reply.
const NO_REPLY_PATTERN = /^(?:no|do[-_.]?not)[-_.]?reply/;

// Exactly two or three alphabetic parts of two letters or more, joined by one `.` or `_`.
// `p.oyelaran` (an initial) and `janedoe` (no separator) are too risky to split.
const PERSON_LOCAL_PART_PATTERN = /^[a-z]{2,}(?:[._][a-z]{2,}){1,2}$/i;

// The strict rule (what Salesforce may be given) also refuses topic and team words that read as
// a name in an address: `growth.team@`, `voice.ai@`, `data.science@`. Kept in step with
// STRICT_REFUSED_PARTS in contact_names.py.
const STRICT_REFUSED_PARTS = new Set([
  ...SHARED_INBOX_LOCAL_PARTS,
  'experience', 'acquisition', 'updates', 'science', 'ai', 'voice', 'data', 'product', 'marketing', 'growth',
  'partnerships', 'recruiting', 'talent', 'careers', 'press', 'media', 'legal', 'finance', 'billing', 'success',
  'service', 'security', 'dev', 'devrel', 'research', 'labs', 'ops', 'hr', 'people', 'team', 'hello', 'contact',
  'admin', 'news', 'events',
]);

/** The name fields a contact may carry besides `name` (book.py sends both on contacts). */
export interface NameFields { first_name?: string | null; last_name?: string | null }

export interface ContactDisplay {
  // What to show: a name, else the address, else a placeholder.
  text: string;
  // False when `text` is only an address or a placeholder, so the surface mutes it.
  named: boolean;
}

/** The part before the `@`, lowercased, without a `+tag`. Empty for no address. */
function localPartOf(email: string | null | undefined): string {
  const localPart = (email ?? '').trim().split('@')[0] ?? '';
  return (localPart.split('+')[0] ?? '').toLowerCase();
}

/** Whether an address is a team or role inbox rather than a person: `sales@`, `sales.team@`, `no-reply@`. */
export function isSharedInbox(email: string | null | undefined): boolean {
  if (!email || !email.includes('@')) return false;
  const localPart = localPartOf(email);
  if (NO_REPLY_PATTERN.test(localPart)) return true;

  // A known compound with its separators dropped: `dev.rel`, `account-manager`, `contact_us`.
  const words = localPart.split(/[._-]/).filter(Boolean);
  if (SHARED_INBOX_LOCAL_PARTS.has(words.join(''))) return true;

  // Every word a role word: `sales`, `sales.team`, `customer-success`.
  return words.length > 0 && words.every(word => SHARED_INBOX_LOCAL_PARTS.has(word));
}

/**
 * `jane.doe@x.com` → "Jane Doe"; null unless the address clearly spells a person's name.
 * `strict` (a Salesforce save) wants exactly a given and a family name, neither a role or topic word.
 */
export function personNameFromEmail(email: string | null | undefined, strict = false): string | null {
  if (!email || !email.includes('@') || isSharedInbox(email)) return null;
  const localPart = localPartOf(email);
  if (!PERSON_LOCAL_PART_PATTERN.test(localPart)) return null;

  // A role word anywhere (`jane.support`) means it may not be a person after all.
  const parts = localPart.split(/[._]/);
  const refused = strict ? STRICT_REFUSED_PARTS : SHARED_INBOX_LOCAL_PARTS;
  if (parts.some(part => refused.has(part))) return null;
  if (strict && parts.length !== 2) return null;

  // Title-case each part; the address already lowercased them.
  return parts.map(part => part.slice(0, 1).toUpperCase() + part.slice(1)).join(' ');
}

/**
 * A name without the address some playground people carry in it: "Paul paul@fleetworks.ai" is
 * "Paul". Null when nothing but an address is left.
 */
export function cleanName(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const words = raw.split(/\s+/u).filter(word => word && !word.includes('@'));
  return words.length ? words.join(' ') : null;
}

/** Whether a name is Apollo's masked preview ("Ravi Pe***r"): shown, but not a name to greet. */
export function isMaskedName(name: string | null | undefined): boolean {
  return Boolean(name && name.includes('*'));
}

/** First and last name fields joined, unless empty or masked. */
function nameFromFields(fields: NameFields | undefined): string | null {
  const joined = [fields?.first_name, fields?.last_name].map(part => part?.trim() ?? '').filter(Boolean).join(' ');
  return joined && !isMaskedName(joined) ? joined : null;
}

/** The name to show for a contact: real, else the first/last name fields, else derived from a
 * `first.last` address, else the address, else a masked name (muted, "not revealed yet"), else
 * the placeholder. */
export function contactDisplay(name: string | null | undefined, email: string | null | undefined, placeholder = 'Unnamed',
                               fields?: NameFields): ContactDisplay {
  const cleaned = cleanName(name);
  const masked = isMaskedName(cleaned);
  const realName = (masked ? null : cleaned) ?? nameFromFields(fields) ?? personNameFromEmail(email);
  if (realName) return { text: realName, named: true };

  // No name to show: the address, muted, else the masked name, else the placeholder.
  const address = email?.trim() || (name?.includes('@') ? name.trim() : '');
  return { text: address || (masked ? cleaned! : placeholder), named: false };
}

/** Just the text, for a sentence or a comma-separated list. */
export function contactDisplayName(name: string | null | undefined, email: string | null | undefined, placeholder = 'Unnamed'): string {
  return contactDisplay(name, email, placeholder).text;
}

/**
 * Whether a person has any name to greet them by: a real (unmasked) name, which an accepted
 * Find name becomes, else the name fields, else one read off a `first.last` address (on the
 * strict rule when `strict`). Mirrors `contact_names.usable_name`.
 */
export function hasUsableName(person: { name?: string | null; email?: string | null } & NameFields, strict = false): boolean {
  const cleaned = cleanName(person.name);
  if (cleaned && !isMaskedName(cleaned)) return true;
  if (nameFromFields(person)) return true;
  return personNameFromEmail(person.email, strict) !== null;
}
